import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
  useMemo,
  useCallback,
  useRef,
} from "react";

import { adminSettingsService } from "../api/service/admin/admin_settings.service";
import { AdminSettings } from "../api/interface/admin_settings.interface";

import { useAuth } from "./AuthContext";
import { hasAnyRole } from "../utils/role";

import { servicesService } from "../api/service/admin/admin_services.service";
import { Service } from "../api/interface/services.interface";

import { adminProjectCategoriesService } from "../api/service/admin/admin_projectcategories.service";
import { ProjectCategory } from "../api/interface/projectcategories.interface";

import { adminProductCategoriesService } from "../api/service/admin/admin_productcategories.service";
import { ProductCategory } from "../api/interface/productcategories.interface";

import { adminProjectsService } from "../api/service/admin/admin_projects.service";
import { Project } from "../api/interface/projects.interface";

import { adminProductsService } from "../api/service/admin/admin_products.service";
import { Product } from "../api/interface/products.interface";


import { adminUsersService } from "../api/service/admin/admin_users.service";
import { adminRolesService } from "../api/service/admin/admin_roles.service";
import { User } from "../api/interface/user.interface";
import { Role } from "../api/interface/roles.interface";



// ===============================
// TYPES
// ===============================
interface AdminContextType {
  settings: AdminSettings | null;
  services: Service[] | null;

  projectCategories: ProjectCategory[] | null;
  productCategories: ProductCategory[] | null;

  projects: Project[] | null;
  products: Product[] | null; // 🔥 NEW

  isLoading: boolean;
  isServicesLoading: boolean;
  isProjectCategoriesLoading: boolean;
  isProductCategoriesLoading: boolean;
  isProjectsLoading: boolean;
  isProductsLoading: boolean; // 🔥 NEW

  refreshSettings: () => Promise<void>;
  refreshServices: () => Promise<void>;
  refreshProjectCategories: () => Promise<void>;
  refreshProductCategories: () => Promise<void>;
  refreshProjects: () => Promise<void>;
  refreshProducts: () => Promise<void>; // 🔥 NEW


  // ...................
  users: User[] | null;
  roles: Role[] | null;
  isUsersLoading: boolean;
  isRolesLoading: boolean;
  refreshUsers: () => Promise<void>;
  refreshRoles: () => Promise<void>;

}

// ===============================
// CONTEXT
// ===============================
const AdminContext = createContext<AdminContextType | undefined>(undefined);

// ===============================
// PROVIDER
// ===============================
export const AdminProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();

  // ===============================
  // SETTINGS
  // ===============================
  const [settings, setSettings] = useState<AdminSettings | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const isFetchingSettingsRef = useRef(false);

  // ===============================
  // SERVICES
  // ===============================
  const [services, setServices] = useState<Service[] | null>(null);
  const [isServicesLoading, setIsServicesLoading] = useState(false);
  const isServicesFetchingRef = useRef(false);

  // ===============================
  // PROJECT CATEGORIES
  // ===============================
  const [projectCategories, setProjectCategories] = useState<ProjectCategory[] | null>(null);
  const [isProjectCategoriesLoading, setIsProjectCategoriesLoading] = useState(false);
  const isProjectCategoriesFetchingRef = useRef(false);

  // ===============================
  // PRODUCT CATEGORIES
  // ===============================
  const [productCategories, setProductCategories] = useState<ProductCategory[] | null>(null);
  const [isProductCategoriesLoading, setIsProductCategoriesLoading] = useState(false);
  const isProductCategoriesFetchingRef = useRef(false);

  // ===============================
  // PROJECTS
  // ===============================
  const [projects, setProjects] = useState<Project[] | null>(null);
  const [isProjectsLoading, setIsProjectsLoading] = useState(false);
  const isProjectsFetchingRef = useRef(false);

  // ===============================
  // PRODUCTS 🔥 NEW
  // ===============================
  const [products, setProducts] = useState<Product[] | null>(null);
  const [isProductsLoading, setIsProductsLoading] = useState(false);
  const isProductsFetchingRef = useRef(false);

  // ===============================
  // Roles and  Users 🔥
  // ===============================
  const [users, setUsers] = useState<User[] | null>(null);
  const [roles, setRoles] = useState<Role[] | null>(null);
  const [isUsersLoading, setIsUsersLoading] = useState(false);
  const [isRolesLoading, setIsRolesLoading] = useState(false);
  const isUsersFetchingRef = useRef(false);
  const isRolesFetchingRef = useRef(false);


  // ===============================
  // SETTINGS LOAD
  // ===============================
  const loadSettings = useCallback(async () => {
    if (isFetchingSettingsRef.current) return;
    isFetchingSettingsRef.current = true;

    try {
      if (!hasAnyRole(user, ["admin", "platform_admin"])) {
        setSettings(null);
        return;
      }

      setIsLoading(true);

      const data = await adminSettingsService.getSettings();
      setSettings(data);
    } catch {
      setSettings(null);
    } finally {
      isFetchingSettingsRef.current = false;
      setIsLoading(false);
    }
  }, [user]);

  // ===============================
  // SERVICES LOAD
  // ===============================
  const loadServices = useCallback(async () => {
    if (isServicesFetchingRef.current) return;
    isServicesFetchingRef.current = true;

    try {
      setIsServicesLoading(true);

      const res = await servicesService.getServices();
      setServices(res.data.services);
    } catch {
      setServices(null);
    } finally {
      isServicesFetchingRef.current = false;
      setIsServicesLoading(false);
    }
  }, []);

  // ===============================
  // PROJECT CATEGORIES LOAD
  // ===============================
  const loadProjectCategories = useCallback(async () => {
    if (isProjectCategoriesFetchingRef.current) return;
    isProjectCategoriesFetchingRef.current = true;

    try {
      setIsProjectCategoriesLoading(true);

      const res = await adminProjectCategoriesService.getProjectCategories();
      setProjectCategories(res.data.categories);
    } catch {
      setProjectCategories(null);
    } finally {
      isProjectCategoriesFetchingRef.current = false;
      setIsProjectCategoriesLoading(false);
    }
  }, []);

  // ===============================
  // PRODUCT CATEGORIES LOAD
  // ===============================
  const loadProductCategories = useCallback(async () => {
    if (isProductCategoriesFetchingRef.current) return;
    isProductCategoriesFetchingRef.current = true;

    try {
      setIsProductCategoriesLoading(true);

      const res = await adminProductCategoriesService.getProductCategories();
      setProductCategories(res.data.categories);
    } catch {
      setProductCategories(null);
    } finally {
      isProductCategoriesFetchingRef.current = false;
      setIsProductCategoriesLoading(false);
    }
  }, []);

  // ===============================
  // PROJECTS LOAD
  // ===============================
  const loadProjects = useCallback(async () => {
    if (isProjectsFetchingRef.current) return;
    isProjectsFetchingRef.current = true;

    try {
      setIsProjectsLoading(true);

      const res = await adminProjectsService.getProjects();
      setProjects(res.data.projects);
    } catch {
      setProjects(null);
    } finally {
      isProjectsFetchingRef.current = false;
      setIsProjectsLoading(false);
    }
  }, []);

  // ===============================
  // PRODUCTS LOAD 🔥 NEW
  // ===============================
  const loadProducts = useCallback(async () => {
    if (isProductsFetchingRef.current) return;
    isProductsFetchingRef.current = true;

    try {
      setIsProductsLoading(true);

      const res = await adminProductsService.getProducts();
      setProducts(res.data.products);
    } catch {
      setProducts(null);
    } finally {
      isProductsFetchingRef.current = false;
      setIsProductsLoading(false);
    }
  }, []);

  // Fonctions loadUsers, loadRoles
  const loadUsers = useCallback(async () => {
    if (isUsersFetchingRef.current) return;
    isUsersFetchingRef.current = true;
    try {
      setIsUsersLoading(true);
      const res = await adminUsersService.getUsers();
      setUsers(res.data.users);
    } catch (error) {
      console.error(error);
      setUsers(null);
    } finally {
      isUsersFetchingRef.current = false;
      setIsUsersLoading(false);
    }
  }, []);

  const loadRoles = useCallback(async () => {
    if (isRolesFetchingRef.current) return;
    isRolesFetchingRef.current = true;
    try {
      setIsRolesLoading(true);
      const res = await adminRolesService.getRoles();
      setRoles(res.data.roles);
    } catch (error) {
      console.error(error);
      setRoles(null);
    } finally {
      isRolesFetchingRef.current = false;
      setIsRolesLoading(false);
    }
  }, []);

  // ===============================
  // REFRESH METHODS
  // ===============================
  const refreshSettings = useCallback(async () => {
    await loadSettings();
  }, [loadSettings]);

  const refreshServices = useCallback(async () => {
    await loadServices();
  }, [loadServices]);

  const refreshProjectCategories = useCallback(async () => {
    await loadProjectCategories();
  }, [loadProjectCategories]);

  const refreshProductCategories = useCallback(async () => {
    await loadProductCategories();
  }, [loadProductCategories]);

  const refreshProjects = useCallback(async () => {
    await loadProjects();
  }, [loadProjects]);

  const refreshProducts = useCallback(async () => {
    await loadProducts();
  }, [loadProducts]);

  // refresh methods
  const refreshUsers = useCallback(async () => { await loadUsers(); }, [loadUsers]);
  const refreshRoles = useCallback(async () => { await loadRoles(); }, [loadRoles]);


  // ===============================
  // INIT
  // ===============================
  useEffect(() => {
    loadSettings();
    loadServices();
    loadProjectCategories();
    loadProductCategories();
    loadProjects();
    loadProducts();
    loadUsers();
    loadRoles();
  }, [
    loadSettings,
    loadServices,
    loadProjectCategories,
    loadProductCategories,
    loadProjects,
    loadProducts,
    loadUsers,
    loadRoles,
  ]);

  // ===============================
  // VALUE
  // ===============================
  const value = useMemo(
    () => ({
      settings,
      services,
      projectCategories,
      productCategories,
      projects,
      products,

      isLoading,
      isServicesLoading,
      isProjectCategoriesLoading,
      isProductCategoriesLoading,
      isProjectsLoading,
      isProductsLoading,

      refreshSettings,
      refreshServices,
      refreshProjectCategories,
      refreshProductCategories,
      refreshProjects,
      refreshProducts,

      users,
      roles,
      isUsersLoading,
      isRolesLoading,
      refreshUsers,
      refreshRoles,
    }),
    [
      settings,
      services,
      projectCategories,
      productCategories,
      projects,
      products,

      isLoading,
      isServicesLoading,
      isProjectCategoriesLoading,
      isProductCategoriesLoading,
      isProjectsLoading,
      isProductsLoading,

      refreshSettings,
      refreshServices,
      refreshProjectCategories,
      refreshProductCategories,
      refreshProjects,
      refreshProducts,

      users,
      roles,
      isUsersLoading,
      isRolesLoading,
      refreshUsers,
      refreshRoles,
    ]
  );

  return (
    <AdminContext.Provider value={value}>
      {children}
    </AdminContext.Provider>
  );
};

// ===============================
// HOOK
// ===============================
export const useAdmin = () => {
  const context = useContext(AdminContext);

  if (!context) {
    throw new Error("useAdmin doit être utilisé dans AdminProvider");
  }

  return context;
};